import { ChangeEvent, useState } from 'react';

import { ISSUE_ATTACHMENT_MAX_FILES, ISSUE_PHOTO_MAX_FILES, IssueSeverity, IssueStatus, RecordState } from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { Control, Path, PathValue } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { SingleValue } from 'react-select';
import { z } from 'zod';

import { cn } from '@/lib/utils';
import { convertImageUrl } from '@/utils/image-url';

import CameraIcon from '../icons/camera';
import DeleteIcon from '../icons/delete';
import { Dropzone, DropzoneContent, DropzoneDescription, DropzoneInput, DropzoneTitle } from '../ui/dropzone';
import { FormFieldItem } from '../ui/form';
import { Input } from '../ui/input';
import { Lightbox } from '../ui/lightbox';
import { Select } from '../ui/select';
import { Textarea } from '../ui/textarea';

export const createIssueFormSchema = (requirePhotos: boolean) => {
  const photosSchema = z.array(z.string()).max(ISSUE_PHOTO_MAX_FILES, { message: `Maximum ${ISSUE_PHOTO_MAX_FILES} photos` });

  return z.object({
    title: z.string().min(1, { message: 'Required' }),
    description: z.string().optional(),
    fieldReference: z.string().optional(),
    severity: z.nativeEnum(IssueSeverity, { message: 'Severity level required' }),
    createdAt: z.string().optional(),
    issueId: z.string().optional(),
    photos: requirePhotos ? photosSchema.min(1, { message: 'At least one photo is required' }) : photosSchema,
    status: z.nativeEnum(IssueStatus),
    asset: z.object({
      label: z.string(),
      value: z.string(),
      locationId: z.string().nullable(),
    }),
    attachments: z
      .array(z.object({ name: z.string().min(1), url: z.string().url(), state: z.nativeEnum(RecordState).optional() }))
      .max(ISSUE_ATTACHMENT_MAX_FILES, { message: `Maximum ${ISSUE_ATTACHMENT_MAX_FILES} attachments` }),
    tags: z.array(z.string()),
    category: z
      .object({
        label: z.string(),
        value: z.string(),
      })
      .optional(),
    location: z
      .object({
        label: z.string(),
        value: z.string(),
      })
      .nullable()
      .optional(),
    closedAt: z.date().nullable().optional(),
    closedByUser: z
      .object({
        label: z.string(),
        value: z.string(),
        avatarUrl: z.string().optional(),
      })
      .nullable()
      .optional(),
  });
};

export type IssueFormSchemaType = z.infer<ReturnType<typeof createIssueFormSchema>>;

export function IssueSelectField({
  label,
  name,
  control,
  options,
  required,
  onChange: onChangeOverride,
  labelSize,
  disabled,
  readonly,
}: {
  label: string;
  name: Path<IssueFormSchemaType>;
  control: Control<IssueFormSchemaType>;
  options: { label: string; value: string | IssueSeverity }[];
  required?: boolean;
  onChange?: (value: string | number) => void;
  labelSize?: 'base' | 'small';
  disabled?: boolean;
  readonly?: boolean;
}) {
  return (
    <FormFieldItem<IssueFormSchemaType, Path<IssueFormSchemaType>> control={control} name={name} label={label} required={required} labelSize={labelSize}>
      {({ value, onChange, error }) => (
        <Select<{ label: string; value: string | IssueSeverity }>
          value={options.find((opt) => opt.value === value)}
          onChange={(val: SingleValue<{ label: string; value: string | IssueSeverity }>) => {
            if (onChangeOverride) {
              onChangeOverride(val?.value ?? '');
            } else {
              onChange(val?.value as PathValue<IssueFormSchemaType, Path<IssueFormSchemaType>>);
            }
          }}
          options={options}
          className={cn('disabled:bg-grey-1 w-full')}
          styles={{
            control: (styles) => ({
              ...styles,
              borderColor: error ? 'rgb(var(--default-red))' : styles.borderColor,
              backgroundColor: readonly ? 'rgb(var(--grey-1))' : styles.backgroundColor,
            }),
          }}
          blurInputOnSelect
          isDisabled={disabled || readonly}
        />
      )}
    </FormFieldItem>
  );
}

export function IssueInputField({
  label,
  name,
  control,
  placeholder,
  required,
  disabled,
  className,
  labelSize,
  readonly,
}: {
  label: string;
  name: Path<IssueFormSchemaType>;
  control: Control<IssueFormSchemaType>;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  labelSize?: 'base' | 'small';
  readonly?: boolean;
}) {
  return (
    <FormFieldItem<IssueFormSchemaType, Path<IssueFormSchemaType>>
      control={control}
      name={name}
      label={label}
      required={required}
      labelSize={labelSize}
      className={className}>
      {({ value, onChange, error }) => (
        <Input
          className="disabled:bg-grey-1"
          placeholder={placeholder ?? `Enter ${label}...`}
          value={value as string}
          onChange={onChange as unknown as (e: ChangeEvent<HTMLInputElement>) => void}
          error={!!error}
          disabled={disabled || readonly}
        />
      )}
    </FormFieldItem>
  );
}

export function IssueTextareaField({
  label,
  name,
  control,
  placeholder,
  required,
  className,
  labelSize,
  readonly,
}: {
  label: string;
  name: Path<IssueFormSchemaType>;
  control: Control<IssueFormSchemaType>;
  placeholder?: string;
  required?: boolean;
  className?: string;
  labelSize?: 'base' | 'small';
  readonly?: boolean;
}) {
  return (
    <FormFieldItem<IssueFormSchemaType, Path<IssueFormSchemaType>>
      control={control}
      name={name}
      label={label}
      required={required}
      labelSize={labelSize}
      className={className}>
      {({ value, onChange, error }) => (
        <Textarea
          className={cn('min-h-[100px] resize-none', readonly && 'bg-grey-1')}
          placeholder={placeholder ?? `Enter ${label}...`}
          value={value as string}
          onChange={onChange as unknown as (e: ChangeEvent<HTMLTextAreaElement>) => void}
          error={!!error}
          disabled={readonly}
        />
      )}
    </FormFieldItem>
  );
}

export function IssuePhotoField({
  label,
  name,
  control,
  required,
  className,
  labelSize,
  readonly,
}: {
  label: string;
  name: Path<IssueFormSchemaType>;
  control: Control<IssueFormSchemaType>;
  required?: boolean;
  className?: string;
  labelSize?: 'base' | 'small';
  readonly?: boolean;
}) {
  const [showLightbox, setShowLightbox] = useState<string[] | undefined>(undefined);
  return (
    <>
      <FormFieldItem<IssueFormSchemaType, Path<IssueFormSchemaType>>
        control={control}
        name={name}
        label={label}
        required={required}
        labelSize={labelSize}
        className={className}>
        {({ value, onChange, error }) => (
          <div className="flex w-full flex-col gap-2">
            {readonly && (
              <div className="flex flex-wrap justify-center gap-4">
                {(value as string[])?.length > 0 ? (
                  (value as string[]).map((photo, index) => {
                    const isBase64 = photo.startsWith('data:');
                    const imageSrc = isBase64 ? photo : convertImageUrl(photo, { width: 120, height: 120 });
                    return (
                      <div key={index} className="relative">
                        <img
                          src={imageSrc}
                          alt=""
                          className="h-[120px] w-[120px] cursor-pointer rounded object-cover"
                          onClick={() => {
                            const lightboxPhotos = (value as string[]).map((p) => (p.startsWith('data:') ? p : convertImageUrl(p)));
                            setShowLightbox(lightboxPhotos);
                          }}
                        />
                      </div>
                    );
                  })
                ) : (
                  <div className="m-1">
                    <i className="text-muted">No photos</i>
                  </div>
                )}
              </div>
            )}

            {!readonly && (
              <>
                <Dropzone
                  accept={{
                    'image/jpeg': ['.jpg', '.jpeg'],
                    'image/png': ['.png'],
                  }}
                  maxFiles={5}
                  onDropAccepted={(files) => {
                    const filePromises = files.map((file) => {
                      return new Promise<string>((resolve, reject) => {
                        const reader = new FileReader();
                        reader.onload = (e) => resolve(e.target?.result as string);
                        reader.onerror = () => reject(reader.error);
                        reader.readAsDataURL(file);
                      });
                    });

                    Promise.all(filePromises)
                      .then((newPhotos) => {
                        onChange([...((value as string[]) || []), ...newPhotos]);
                      })
                      .catch((error) => {
                        console.error('Failed to read photo:', error);
                        toast.error('Could not read one of the photos. Please try again.');
                      });
                  }}
                  onDropRejected={(fileRejections) => {
                    const rejection = fileRejections[0];
                    if (rejection.errors.some((e) => e.code === 'file-invalid-type')) {
                      toast.error('File type is not supported.');
                    } else if (rejection.errors.some((e) => e.code === 'file-too-large')) {
                      toast.error('File exceeds 2MB.');
                    }
                  }}>
                  <DropzoneContent
                    className={cn(
                      'border-grey-4 flex w-full flex-col items-center justify-center gap-0 border-2 border-dashed p-4 text-center',
                      error && 'border-default-red',
                      readonly && 'bg-grey-1 opacity-50',
                    )}>
                    {(value as string[])?.length > 0 ? (
                      <div className="flex flex-wrap justify-center gap-4">
                        {(value as string[]).map((photo, index) => {
                          const isBase64 = photo.startsWith('data:');
                          const imageSrc = isBase64 ? photo : convertImageUrl(photo, { width: 120, height: 120 });
                          return (
                            <div key={index} className="relative">
                              <img src={imageSrc} alt="" className="h-[120px] w-[120px] rounded object-cover" />
                              <Button
                                size="icon"
                                type="button"
                                variant="destructive"
                                aria-label="Remove photo"
                                className="absolute -top-1 -right-1 size-4"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const newPhotos = [...(value as string[])];
                                  newPhotos.splice(index, 1);
                                  onChange(newPhotos);
                                }}>
                                <DeleteIcon className="size-3.5" />
                              </Button>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <>
                        <DropzoneTitle>
                          <CameraIcon className="text-grey-5 size-8" />
                        </DropzoneTitle>
                        <DropzoneDescription className="text-grey-5">Drag photos here or click to upload *</DropzoneDescription>
                      </>
                    )}
                    <DropzoneInput />
                  </DropzoneContent>
                </Dropzone>
                <div className="text-grey-5 text-xs">* JPG & PNG file types supported. Max size of 2,000kB.</div>
              </>
            )}
          </div>
        )}
      </FormFieldItem>
      {showLightbox && (
        <Lightbox
          photos={showLightbox.map((photo) => (photo.startsWith('data:') ? photo : convertImageUrl(photo)))}
          onHide={() => setShowLightbox(undefined)}
        />
      )}
    </>
  );
}
